import { LocalizationSerializer } from "@serializers/localization"
import { ArticleSerializer } from "@serializers/article"

import { BaseHttpProvider } from "./base-http.provider"

import type { AxiosError } from "axios"
import type { LanguageISO } from "./types/lang"

export class SaferDatingProvider extends BaseHttpProvider {
  async getSaferDatingPage(locale: LanguageISO) {
    try {
      const data = await this.execute({
        uri: "/safer-dating",
        query: {
          _locale: locale, // eslint-disable-line
        },
      })

      return {
        templates: data.templates.map((template) =>
          LocalizationSerializer(template),
        ),
        // The languages this page exists in, so its footer switcher offers
        // only those rather than the site-wide list.
        availableLanguages: (data.available_languages ?? []) as LanguageISO[],
        featuredPost: data.featured_post
          ? ArticleSerializer({ post: data.featured_post })
          : null,
        // Empty to "", never null. None of these fields is required in Strapi,
        // so a page published in a new language can arrive with them unset, and
        // a null reaches Next's Link as its href and throws. That would take
        // the whole page down for one missing link.
        branchLinks: {
          main_footer_app_store: data?.footer_app_store_link ?? "",
          main_footer_google_play: data?.footer_google_play_link ?? "",
          main_footer_app_gallery: data?.footer_app_gallery_link ?? "",
          sidebar_button: data?.sidebar_button_link ?? "",
          page_banner_button: data?.page_banner_button_link ?? "",
          smart_banner_link: data?.smart_banner_link ?? "",
          smart_banner_app_gallery_link:
            data?.smart_banner_app_gallery_link ?? "",
          smart_banner_playmarket_link:
            data?.smart_banner_app_playmarket_link ?? "",
        },
      }
    } catch (err) {
      if ((err as AxiosError).response) {
        const error = err as AxiosError
        console.error(error.response) // eslint-disable-line no-console
      }
      throw err
    }
  }

  async getPosts(params: {
    lang: LanguageISO
    page: number
    pageSize: number
    countryCode?: string
  }) {
    const { lang = "ru", page = 1, pageSize = 7, countryCode } = params
    const response = await this.execute({
      uri: "/safer-dating-posts",
      query: {
        lang,
        page,
        pageSize,
        ...(countryCode && { country_code: countryCode }),
      },
    })
    // @ts-ignore
    const data = response.data.map((post) => ArticleSerializer({ post }))

    return {
      data,
      pagination: response.meta.pagination,
    }
  }

  async getPost(lang: LanguageISO, slug: string, countryCode?: string) {
    try {
      const data = await this.execute({
        uri: `/safer-dating-posts/${slug}`,
        query: {
          lang,
          ...(countryCode && { country_code: countryCode }),
        },
      })
      return ArticleSerializer({ post: data, shorted: false })
    } catch (err) {
      throw err
    }
  }

  async getPostsBySlug(slug: string, countryCode?: string) {
    try {
      const { data } = await this.execute({
        uri: `/safer-dating-posts/slugs/${slug}`,
        query: {
          ...(countryCode && { country_code: countryCode }),
        },
      })
      // @ts-ignore
      const posts = data.map((post) => ArticleSerializer({ post }))
      return { posts }
    } catch (err) {
      throw err
    }
  }
}
